import React from 'react';
import { Lock, Flame, TrendingUp } from 'lucide-react';

const features = [
  {
    icon: <Lock size={24} />,
    title: 'Commitment Timer + Strict Mode',
    description: "Set a goal, set a duration, start the clock. Enable Strict Mode and the app locks you in — navigation blocked, focus enforced. The timer doesn't care how you feel. It just runs.",
    detail: '92% of users enable Strict Mode on their most important tasks.',
    accent: '#7C3AED',
  },
  {
    icon: <Flame size={24} />,
    title: 'Streak Tracking',
    description: "Every day you complete at least one commitment, your streak grows. Every day you don't, you see it. The streak isn't a reward — it's a mirror.",
    detail: '21-day streaks correlate with lasting habit formation.',
    accent: 'rgba(255, 255, 255, 0.5)',
  },
  {
    icon: <TrendingUp size={24} />,
    title: 'Honest Analytics',
    description: "Your success rate, total focus hours, failure patterns — all of it, plainly displayed. Most apps show only your wins. TickBoom shows the full picture.",
    detail: 'Track success rates, streaks, and time patterns.',
    accent: 'rgba(255, 255, 255, 0.5)',
  }
];

const Features = () => {
  return (
    <section id="features" className="relative py-32 px-6 lg:px-12" style={{ background: '#050508' }}>
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-20 reveal">
          <div className="section-label">Core Features</div>
          <h2 className="text-4xl lg:text-6xl font-black tracking-tight mb-5">
            Three things.{' '}
            <span className="gradient-text">Done right.</span>
          </h2>
          <p className="text-lg text-[rgba(255,255,255,0.4)] max-w-xl mx-auto">
            No feature bloat. TickBoom does three things extremely well — and that's enough to change your habits.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`cyber-card p-10 flex flex-col reveal delay-${index * 100 + 100}`}
              style={index === 0 ? { borderColor: 'rgba(124, 58, 237, 0.3)' } : {}}
            >
              {/* Icon */}
              <div
                className="w-10 h-10 mb-8 flex items-center justify-center rounded-lg"
                style={{
                  color: feature.accent,
                  background: index === 0 ? 'rgba(124, 58, 237, 0.12)' : 'rgba(255,255,255,0.06)'
                }}
              >
                {feature.icon}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold mb-4 text-white leading-snug">
                {feature.title}
              </h3>
              <p className="text-[rgba(255,255,255,0.5)] leading-relaxed mb-8 flex-1 text-sm">
                {feature.description}
              </p>

              {/* Stat */}
              <div className="flex items-center gap-2 pt-5 border-t border-[rgba(255,255,255,0.06)]">
                <div
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: index === 0 ? '#7C3AED' : 'rgba(255,255,255,0.3)' }}
                ></div>
                <span className="text-xs text-[rgba(255,255,255,0.35)] font-mono">
                  {feature.detail}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
